import * as HLF from "html-living-framework";
import {Application} from "../Application";
import {Callback} from "./Reflection";

export class AudioChannel {

    public static readonly BGM = new AudioChannel(HLF.AudioCategory.BGM);
    public static readonly SFX = new AudioChannel(HLF.AudioCategory.SFX);
    private static _muteVolume = 1;

    private readonly _category: string;
    private readonly _currentAudio: { [index: string]: boolean } = {};

    constructor(category: string) {
        this._category = category;
    }

    static get volume(): number {
        return Application.instance.audio.masterVolume;
    }

    static set volume(value: number) {
        Application.instance.audio.masterVolume = value;
    }

    static get mute() {
        return Application.instance.audio.masterVolume === 0;
    }

    static set mute(value: boolean) {
        if (value) {
            Application.instance.audio.masterVolume = 0;
        } else {
            Application.instance.audio.masterVolume = this._muteVolume;
        }
    }

    static get muteVolume(): number {
        return this._muteVolume;
    }

    static set muteVolume(value: number) {
        this._muteVolume = value;
    }

    get volume(): number {
        return Application.instance.audio.getCategoryVolume(this._category);
    }

    set volume(value: number) {
        Application.instance.audio.setCategoryVolume(this._category, value);
    }

    // tslint:disable: unified-signatures
    public play(id: string | string[]): void;
    public play(id: string | string[], cb: Callback): void;
    public play(id: string | string[], volume: number): void;
    public play(id: string | string[], volume: number, cb: Callback): void;
    public play(id: string | string[], loop: "loop"): void;
    public play(id: string | string[], volume: number, loop: "loop"): void;
    public play(id: string | string[], arg0?: Callback | number | "loop", arg1?: Callback | "loop") {
        const volume = typeof arg0 === "number" ? arg0 : 1;
        const loopOrCb = typeof arg0 !== "number" ? arg0 : arg1;
        if (typeof id === "string") {
            this.playAudio(id, volume, loopOrCb);
        } else {
            const array = id.slice();
            const onPlay = () => {
                if (array.length > 0) {
                    this.playAudio(array.shift()!, volume, onPlay);
                } else if (typeof loopOrCb === "function") {
                    loopOrCb();
                }
            };
            onPlay();
        }
    }

    public stop(id?: string | string[]) {
        if (typeof id === "string") {
            const audio = Application.instance.audio;
            audio.stop(id, this._category);
            if (this._currentAudio[id]) {
                delete this._currentAudio[id];
                const track = audio.getAudioTrack(id, this._category);
                if (track) {
                    track.off(HLF.HowlerUtils.Events.END);
                }
            }
        } else if (id) {
            for (const i of id) {
                this.stop(i);
            }
        } else {
            for (const p of Object.getOwnPropertyNames(this._currentAudio)) {
                this.stop(p);
            }
        }
    }

    public get(id: string) {
        return Application.instance.audio.getAudioTrack(id, this._category);
    }

    public load(id: string | string[], cb?: Callback) {
        Application.instance.audio.load(id, this._category, cb);
    }

    public unload(id: string | string[]) {
        if (typeof id === "string") {
            Application.instance.audio.unload(id, this._category, true);
        } else {
            for (const i of id) {
                this.unload(i);
            }
        }
    }

    private playAudio(id: string, volume: number, cb?: Callback | "loop") {
        const audio = Application.instance.audio;
        audio.play(id, volume, typeof cb === "string", this._category);
        const track = audio.getAudioTrack(id, this._category);
        if (track) {
            this._currentAudio[id] = true;
            track.once(HLF.HowlerUtils.Events.END, () => {
                this._currentAudio[id] = false;
                if (typeof cb === "function") {
                    cb();
                }
            });
        }
    }
}
